import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Skeleton,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  AccessTime as SessionIcon,
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import SessionQuotaManager from '../components/SessionQuotaManager';

const AdminSessionQuotasPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showQuotaManager, setShowQuotaManager] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchFacilities();
    }
  }, [user]);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/facilities/all');
      setFacilities(response.data.data || []);
      
    } catch (error) {
      setError('Failed to load facilities');
      console.error('Error fetching facilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageQuotas = (facility) => {
    setSelectedFacility(facility);
    setShowQuotaManager(true);
  };

  const handleCloseQuotaManager = () => {
    setShowQuotaManager(false);
    setSelectedFacility(null);
    // Refresh facilities to get updated quota status
    fetchFacilities();
  };

  const getQuotaStatus = (facility) => {
    if (!facility.sessionQuotas) {
      return { enabled: false, sessionCount: 0 };
    }
    
    return {
      enabled: facility.sessionQuotas.enabled || false,
      sessionCount: facility.sessionQuotas.sessions?.length || 0
    };
  };

  const getStatusColor = (facility) => {
    const status = getQuotaStatus(facility);
    if (!status.enabled) return 'default';
    if (status.sessionCount === 0) return 'warning';
    return 'success';
  };

  const getStatusLabel = (facility) => {
    const status = getQuotaStatus(facility);
    if (!status.enabled) return t('admin:quotas.status.disabled');
    if (status.sessionCount === 0) return t('admin:quotas.status.enabledNoSessions');
    return t('admin:quotas.status.active', { count: status.sessionCount });
  };

  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          {t('admin:quotas.accessDenied')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/admin')}
            sx={{ mr: 2, bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SessionIcon sx={{ fontSize: 48, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main' }}>
                {t('admin:quotas.title')}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                {t('admin:quotas.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Facilities Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('admin:quotas.facilityQuotas')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('admin:quotas.description')}
          </Typography>

          {loading ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin:quotas.table.facility')}</TableCell>
                    <TableCell>{t('admin:quotas.table.type')}</TableCell>
                    <TableCell>{t('admin:quotas.table.status')}</TableCell>
                    <TableCell>{t('admin:quotas.table.capacity')}</TableCell>
                    <TableCell align="right">{t('admin:quotas.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton width="60%" /></TableCell>
                      <TableCell><Skeleton width={80} /></TableCell>
                      <TableCell><Skeleton width={100} /></TableCell>
                      <TableCell><Skeleton width={60} /></TableCell>
                      <TableCell align="right">
                        <Skeleton width={120} height={36} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('admin:quotas.table.facility')}</TableCell>
                    <TableCell>{t('admin:quotas.table.type')}</TableCell>
                    <TableCell>{t('admin:quotas.table.quotaStatus')}</TableCell>
                    <TableCell>{t('admin:quotas.table.capacity')}</TableCell>
                    <TableCell>{t('admin:quotas.table.status')}</TableCell>
                    <TableCell align="right">{t('admin:quotas.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facilities.map((facility) => (
                    <TableRow key={facility._id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            {facility.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {facility.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={facility.type}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(facility)}
                          color={getStatusColor(facility)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {t('admin:quotas.table.capacityCount', { count: facility.capacity })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={facility.status}
                          color={facility.status === 'active' ? 'success' : 'warning'}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<SettingsIcon />}
                            onClick={() => handleManageQuotas(facility)}
                            disabled={facility.status !== 'active'}
                          >
                            {t('admin:quotas.actions.manage')}
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/facilities/${facility._id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && facilities.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SessionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('admin:quotas.noFacilities.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('admin:quotas.noFacilities.description')}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/admin/facilities')}
                startIcon={<AddIcon />}
              >
                {t('admin:quotas.noFacilities.addFacility')}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Session Quota Manager Dialog */}
      <Dialog 
        open={showQuotaManager}
        onClose={handleCloseQuotaManager}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: { 
            width: '95vw',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: '900px'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SessionIcon sx={{ mr: 1 }} />
{t('admin:quotas.dialog.title')}
            </Box>
            <IconButton onClick={handleCloseQuotaManager}>
              <ArrowBackIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedFacility && (
            <SessionQuotaManager 
              facilityId={selectedFacility._id}
              facilityName={selectedFacility.name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Stats Cards */}
      {!loading && facilities.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary" gutterBottom>
                  {facilities.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:quotas.stats.totalFacilities')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main" gutterBottom>
                  {facilities.filter(f => getQuotaStatus(f).enabled).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:quotas.stats.quotasEnabled')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main" gutterBottom>
                  {facilities.reduce((total, f) => total + getQuotaStatus(f).sessionCount, 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:quotas.stats.totalSessions')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main" gutterBottom>
                  {facilities.filter(f => f.status === 'active').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:quotas.stats.activeFacilities')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default AdminSessionQuotasPage;